import { useEffect, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ChevronDown, ChevronRight, Eye, EyeOff, FileText, Filter } from 'lucide-react'
import { getAggregatorPortfolioProjects, getAggregatorDisclosures, bulkSetDiscoverable } from '../../services/api'
import type { AggregatorProject, AggregatorEntity, DisclosureAgreement } from '../../types'
import { useAggregator } from '../../contexts/AggregatorContext'
import { DOMAIN_OPTIONS, STAGE_OPTIONS } from '../../constants/aggregator'
import type { DomainOption, StageOption } from '../../constants/aggregator'
import { useAggregatorPreferences } from '../../hooks/useAggregatorPreferences'
import type { EntityTags, FilterState } from '../../hooks/useAggregatorPreferences'
import { useOrgType } from '../../contexts/OrgTypeContext'
import { cameInByInvitation } from '../../lib/portfolioMembership'
import EntityBadge from './EntityBadge'
import InboundTriagePanel from './InboundTriagePanel'
import FilterPanel from './FilterPanel'
import BulkActionBar from './BulkActionBar'
import LoadingSkeleton from '../shared/LoadingSkeleton'

type GroupBy = 'entity' | 'vertical' | 'stage' | 'date' | 'status'

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'entity', label: 'By Entity' },
  { value: 'vertical', label: 'By Vertical' },
  { value: 'stage', label: 'By Stage' },
  { value: 'date', label: 'By Date' },
  { value: 'status', label: 'By Status' },
]

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-500/20 text-green-700',
  draft: 'bg-sky-500/20 text-sky-700',
  archived: 'bg-amber-500/20 text-amber-700',
  discoverable: 'bg-iflow-500/20 text-iflow-700',
}

function getStatusBand(p: AggregatorProject): string {
  if (p.discoverable) return 'Discoverable'
  if (p.status === 'archived') return 'Archived'
  return 'Active'
}

function getDateBand(createdAt: string): string {
  const now = new Date()
  const created = new Date(createdAt)
  const diffMs = now.getTime() - created.getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)
  if (diffDays <= 31) return 'This month'
  if (diffDays <= 92) return 'Last 3 months'
  return 'Older'
}

function applyFilters(
  projects: AggregatorProject[],
  filters: FilterState,
  entityTags: EntityTags,
): AggregatorProject[] {
  return projects.filter((p) => {
    if (filters.domains.length > 0) {
      const domain = entityTags[p.entity_id]?.domain
      if (!domain || !filters.domains.includes(domain)) return false
    }
    if (filters.stages.length > 0) {
      const stage = entityTags[p.entity_id]?.stage
      if (!stage || !filters.stages.includes(stage)) return false
    }
    if (filters.statuses.length > 0) {
      const band = getStatusBand(p).toLowerCase() as 'discoverable' | 'active' | 'archived'
      if (!filters.statuses.includes(band)) return false
    }
    if (filters.entityIds.length > 0) {
      if (!filters.entityIds.includes(p.entity_id)) return false
    }
    return true
  })
}

function groupProjects(
  projects: AggregatorProject[],
  groupBy: GroupBy,
  entityTags: EntityTags,
): Map<string, AggregatorProject[]> {
  const map = new Map<string, AggregatorProject[]>()
  for (const p of projects) {
    let key: string
    switch (groupBy) {
      case 'entity':
        key = p.entity_id
        break
      case 'vertical':
        key = entityTags[p.entity_id]?.domain ?? '__unassigned__'
        break
      case 'stage':
        key = entityTags[p.entity_id]?.stage ?? '__unassigned__'
        break
      case 'date':
        key = getDateBand(p.created_at)
        break
      case 'status':
        key = getStatusBand(p)
        break
    }
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(p)
  }
  return map
}

function getDisclosureStatus(d: DisclosureAgreement): string {
  if (d.org_a_consented_level === d.org_b_consented_level) return 'Active'
  return 'Pending'
}

function getDisclosurePendingAction(d: DisclosureAgreement, entityName: string, counterpartyLabel: string): string {
  const myOrgIsA = d.my_org_id === d.org_a_id
  const myNeedsConsent = myOrgIsA
    ? d.org_a_consented_level < d.org_b_consented_level
    : d.org_b_consented_level < d.org_a_consented_level
  const counterpartyNeedsConsent = myOrgIsA
    ? d.org_b_consented_level < d.org_a_consented_level
    : d.org_a_consented_level < d.org_b_consented_level
  if (myNeedsConsent) return `Awaiting ${entityName} consent`
  if (counterpartyNeedsConsent) return `Awaiting ${counterpartyLabel || 'counterparty'} consent`
  return '—'
}

interface AssignDropdownProps {
  label: string
  options: readonly string[]
  onSelect: (value: string) => void
  currentValue?: string
}

function AssignDropdown({ label, options, onSelect, currentValue }: AssignDropdownProps) {
  return (
    <select
      value={currentValue ?? ''}
      onChange={(e) => { if (e.target.value) onSelect(e.target.value) }}
      className="ml-2 text-xs border border-gray-200 rounded px-1 py-0.5 text-gray-600 bg-white cursor-pointer hover:border-iflow-400 focus:outline-none focus:border-iflow-500"
      aria-label={label}
    >
      <option value="">{currentValue ? currentValue : label}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  )
}

export default function AggregatorDashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { vocab } = useOrgType()
  const { hasAggregatorAccess, activeAggregatorId, memberships } = useAggregator()
  const { groupBy, setGroupBy, filters, setFilters, entityTags, setEntityTag } = useAggregatorPreferences()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set())
  const [disclosuresOpen, setDisclosuresOpen] = useState(false)
  // BUG-121: surface a failure of the discoverability toggle mutation instead of
  // swallowing it (the mutation previously had onSuccess but no onError).
  const [actionError, setActionError] = useState<string | null>(null)

  // BUG-152: the bulk actions were inconsistent when the filter set changed
  // AFTER a selection was made — export/invite operated on the filtered list
  // while mark-hidden/discoverable operated on the raw id set, so the two could
  // act on different projects (some no longer even visible). Clearing the
  // selection whenever the filters change keeps the selection a strict subset of
  // what the user can currently see, so every bulk action targets the same set.
  // Depend on the serialised filters (the object is rebuilt each render from
  // localStorage, so its reference is not stable — a string comparison is).
  const filtersSignature = JSON.stringify(filters)
  useEffect(() => {
    setSelectedProjectIds(new Set())
  }, [filtersSignature])

  // Auto-dismiss the action-error toast so it doesn't linger.
  useEffect(() => {
    if (!actionError) return
    const t = setTimeout(() => setActionError(null), 5000)
    return () => clearTimeout(t)
  }, [actionError])

  const toggleSelectProject = (projectId: string) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }

  const selectAllInGroup = (groupProjectIds: string[]) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev)
      const allSelected = groupProjectIds.every((id) => prev.has(id))
      if (allSelected) {
        groupProjectIds.forEach((id) => next.delete(id))
      } else {
        groupProjectIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const exitSelectionMode = () => {
    setSelectionMode(false)
    setSelectedProjectIds(new Set())
  }

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
    enabled: hasAggregatorAccess,
  })

  const { data: disclosuresData } = useQuery({
    queryKey: ['aggregatorDisclosures', activeAggregatorId],
    queryFn: () => getAggregatorDisclosures(activeAggregatorId ?? ''),
    enabled: hasAggregatorAccess && !!activeAggregatorId,
  })

  // Per-project discoverability toggle for the company view (T8). Routes through
  // the same grant-checked bulk endpoint as the multi-select bar (a one-item
  // list) — NOT the inventor PUT /projects/{id}, which an aggregator account has
  // no per-user grant for and would 403.
  const toggleDiscoverableMutation = useMutation({
    mutationFn: ({ id, discoverable }: { id: string; discoverable: boolean }) => {
      if (!activeAggregatorId) return Promise.reject(new Error('No active aggregator'))
      return bulkSetDiscoverable([id], discoverable, activeAggregatorId)
    },
    onSuccess: () => {
      setActionError(null)
      queryClient.invalidateQueries({ queryKey: ['aggregator-portfolio', activeAggregatorId] })
    },
    // BUG-121: give honest feedback when the toggle fails (e.g. a missing grant
    // 403s) instead of silently leaving the badge unchanged.
    onError: () => {
      setActionError('Could not update discoverability. Please try again.')
    },
  })

  if (!hasAggregatorAccess) {
    return (
      <div className="text-center py-16">
        <p className="text-lg font-medium text-gray-900">No aggregator access</p>
        <p className="text-sm text-gray-600 mt-2">
          You don't have aggregator account access. Projects shared with you will appear here once access is granted.
        </p>
      </div>
    )
  }

  if (isLoading) return <LoadingSkeleton rows={6} />

  if (isError) return (
    <div className="text-center py-16">
      <AlertCircle size={48} className="mx-auto mb-3 text-red-400" />
      <p className="text-lg font-medium text-gray-900">{vocab.loadFailedMessage}</p>
      <p className="text-sm text-gray-600 mt-1">{(error as Error)?.message}</p>
      <button
        onClick={() => refetch()}
        className="mt-4 px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800"
        data-testid="error-retry"
      >
        Retry
      </button>
    </div>
  )

  const projects: AggregatorProject[] = data?.projects ?? []
  const entities: AggregatorEntity[] = data?.entities ?? []
  const count = data?.count ?? 0
  const totalCount = data?.total_count ?? count
  const isPartialView = totalCount > count

  if (count === 0) return (
    <div className="text-center py-16">
      <p className="text-lg font-medium text-gray-900">No projects yet</p>
      <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
        No projects shared with you yet. Inventors will appear here when they grant your aggregator account access.
      </p>
    </div>
  )

  const disclosures: DisclosureAgreement[] = Array.isArray(disclosuresData) ? disclosuresData : []
  const entityMap = new Map(entities.map((e) => [e.entity_id, e]))
  const activeMembership = memberships?.find((m) => m.aggregator_id === activeAggregatorId)
  // Never render a raw aggregator id as its display name. The backend's
  // display-name lookup falls back to the aggregator_id when no name is set, so
  // an unnamed aggregator would surface a bare UUID in this heading (UX review
  // Finding 3). Treat a name that equals the id, or is UUID-shaped, as "unnamed"
  // and use the friendly vocabulary fallback instead.
  const rawAggregatorName = activeMembership?.aggregator_name
  const isUuidLike = (s?: string) =>
    !!s && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
  const aggregatorLabel =
    rawAggregatorName && rawAggregatorName !== activeAggregatorId && !isUuidLike(rawAggregatorName)
      ? rawAggregatorName
      : vocab.collectionFallbackName

  const filteredProjects = applyFilters(projects, filters, entityTags)
  const grouped = groupProjects(filteredProjects, groupBy, entityTags)

  const handleAssignForGroup = (
    groupProjects: AggregatorProject[],
    field: 'domain' | 'stage',
    value: string,
  ) => {
    const uniqueEntityIds = Array.from(new Set(groupProjects.map((p) => p.entity_id)))
    for (const entityId of uniqueEntityIds) {
      if (field === 'domain') {
        setEntityTag(entityId, 'domain', value as DomainOption)
      } else {
        setEntityTag(entityId, 'stage', value as StageOption)
      }
    }
  }

  return (
    <div>
      {/* Managed-by header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="portfolio-header">
          {aggregatorLabel} — {entities.length} {vocab.collectionLabel}{' '}
          {entities.length === 1 ? vocab.entitySingular : vocab.entityPlural}
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          {count} {count === 1 ? 'project' : 'projects'}
          {isPartialView && (
            <span className="ml-2 text-amber-600 text-xs font-medium" data-testid="analyst-scope-indicator">
              ({count} of {totalCount} visible to you)
            </span>
          )}
        </p>
      </div>

      {/* Inbound triage — today's inbound ranked by blind match score */}
      <InboundTriagePanel
        projects={projects}
        onProjectClick={(id) => navigate(`/project/${id}`)}
      />

      {/* GroupBy chips + Filters button + Select toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {GROUP_BY_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            data-testid={`groupby-${opt.value}`}
            onClick={() => setGroupBy(opt.value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              groupBy === opt.value
                ? 'bg-iflow-700 text-white border-iflow-500'
                : 'text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
        <button
          data-testid="filters-btn"
          onClick={() => setFiltersOpen(true)}
          className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
            (filters.domains.length + filters.stages.length + filters.statuses.length + filters.entityIds.length) > 0
              ? 'bg-iflow-50 text-iflow-700 border-iflow-300'
              : 'text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
        >
          <Filter size={14} />
          Filters
          {(filters.domains.length + filters.stages.length + filters.statuses.length + filters.entityIds.length) > 0 && (
            <span className="ml-0.5 bg-iflow-700 text-white text-xs rounded-full px-1.5 py-0.5">
              {filters.domains.length + filters.stages.length + filters.statuses.length + filters.entityIds.length}
            </span>
          )}
        </button>
        <button
          data-testid="selection-mode-toggle"
          onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
            selectionMode
              ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'
              : 'text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
        >
          {selectionMode ? 'Cancel' : 'Select'}
        </button>
      </div>

      {/* Layout with optional filter panel */}
      <div className="flex gap-4">
        {filtersOpen && (
          <FilterPanel
            projects={projects}
            entityTags={entityTags}
            filters={filters}
            setFilters={setFilters}
            onClose={() => setFiltersOpen(false)}
          />
        )}

        <div className="flex-1 min-w-0">
          {/* Empty state after filtering */}
          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">No projects match the current filters.</p>
              <button
                onClick={() => setFilters({ domains: [], stages: [], statuses: [], entityIds: [] })}
                className="mt-2 text-sm text-iflow-600 hover:text-iflow-700 font-medium"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Grouped content */}
          {filteredProjects.length > 0 && (
            <div className="space-y-8">
              {Array.from(grouped.entries()).map(([groupKey, groupItems]) => {
                // Compute group label
                let groupLabel: string
                if (groupBy === 'entity') {
                  const entity = entityMap.get(groupKey)
                  groupLabel = entity?.entity_name ?? groupItems[0]?.entity_name ?? groupKey
                } else if (groupKey === '__unassigned__') {
                  groupLabel = 'Unassigned'
                } else {
                  groupLabel = groupKey
                }

                const isUnassigned = groupKey === '__unassigned__'
                const showAssignDropdown = (groupBy === 'vertical' || groupBy === 'stage') && isUnassigned
                const showValueChip = (groupBy === 'vertical' || groupBy === 'stage') && !isUnassigned

                const groupProjectIds = groupItems.map((p) => p.project_id)
                const allGroupSelected = groupProjectIds.length > 0 && groupProjectIds.every((id) => selectedProjectIds.has(id))

                return (
                  <div key={groupKey} data-testid={`group-${groupKey}`}>
                    <div className="flex items-center gap-2 mb-3">
                      {selectionMode && (
                        <input
                          type="checkbox"
                          data-testid={`group-select-all-${groupKey}`}
                          checked={allGroupSelected}
                          onChange={() => selectAllInGroup(groupProjectIds)}
                          aria-label="Select all in group"
                          className="h-4 w-4 rounded border-gray-300 text-iflow-600 focus:ring-iflow-500 cursor-pointer"
                        />
                      )}
                      {groupBy === 'entity' ? (
                        <>
                          <EntityBadge
                            entityName={groupLabel}
                            // ★ says this inventor is in the portfolio by
                            // invitation — how the relationship began, and
                            // nothing about what they show you.
                            byInvitation={cameInByInvitation(entityMap.get(groupKey))}
                          />
                          <span className="text-sm text-gray-500">
                            {groupItems.length} {groupItems.length === 1 ? 'project' : 'projects'}
                          </span>
                        </>
                      ) : (
                        <>
                          <h2 className="text-sm font-semibold text-gray-800">{groupLabel}</h2>
                          <span className="text-xs text-gray-500">({groupItems.length})</span>
                          {showAssignDropdown && (
                            <AssignDropdown
                              label={groupBy === 'vertical' ? 'Assign vertical →' : 'Assign stage →'}
                              options={groupBy === 'vertical' ? DOMAIN_OPTIONS : STAGE_OPTIONS}
                              onSelect={(val) =>
                                handleAssignForGroup(groupItems, groupBy === 'vertical' ? 'domain' : 'stage', val)
                              }
                            />
                          )}
                          {showValueChip && (
                            <AssignDropdown
                              label={groupKey}
                              options={groupBy === 'vertical' ? DOMAIN_OPTIONS : STAGE_OPTIONS}
                              currentValue={groupKey}
                              onSelect={(val) =>
                                handleAssignForGroup(groupItems, groupBy === 'vertical' ? 'domain' : 'stage', val)
                              }
                            />
                          )}
                        </>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {groupItems.map((project) => (
                        <div
                          key={project.project_id}
                          className={`light-card p-4 ${selectionMode && selectedProjectIds.has(project.project_id) ? 'ring-2 ring-iflow-500' : ''}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            {selectionMode && (
                              <input
                                type="checkbox"
                                data-testid={`project-checkbox-${project.project_id}`}
                                checked={selectedProjectIds.has(project.project_id)}
                                onChange={() => toggleSelectProject(project.project_id)}
                                aria-label={`Select ${project.name}`}
                                className="mt-1 mr-2 h-4 w-4 rounded border-gray-300 text-iflow-600 focus:ring-iflow-500 cursor-pointer flex-shrink-0"
                              />
                            )}
                            <h3 className="text-base font-semibold text-gray-900 line-clamp-1 flex-1">
                              {project.name}
                            </h3>
                            {(activeMembership?.role === 'billing_owner' || activeMembership?.role === 'admin') && getStatusBand(project) !== 'Archived' ? (
                              <button
                                data-testid={`toggle-discoverable-${project.project_id}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleDiscoverableMutation.mutate({ id: project.project_id, discoverable: !project.discoverable })
                                }}
                                disabled={toggleDiscoverableMutation.isPending}
                                title={project.discoverable ? 'Discoverable — click to hide' : 'Hidden — click to make discoverable'}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium disabled:opacity-50 ${STATUS_COLORS[getStatusBand(project).toLowerCase()] ?? STATUS_COLORS[project.status] ?? ''}`}
                              >
                                {project.discoverable ? <Eye size={11} /> : <EyeOff size={11} />}
                                {getStatusBand(project)}
                              </button>
                            ) : (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[getStatusBand(project).toLowerCase()] ?? STATUS_COLORS[project.status] ?? ''}`}>
                                {getStatusBand(project)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{project.description}</p>
                          <div className="flex items-center gap-2 mb-3">
                            <EntityBadge entityName={project.entity_name} />
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <FileText size={11} /> {project.document_count ?? 0}
                            </span>
                          </div>
                          <button
                            onClick={() => navigate(`/project/${project.project_id}`)}
                            className="text-sm font-medium text-iflow-600 hover:text-iflow-700"
                          >
                            Open ▸
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Active Disclosures collapsible section */}
      <div className="mt-8 border border-gray-200 rounded-lg" data-testid="disclosures-section">
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 rounded-lg"
          onClick={() => setDisclosuresOpen((prev) => !prev)}
          data-testid="disclosures-toggle"
          aria-expanded={disclosuresOpen}
        >
          <span>{vocab.activeSectionLabel}{disclosures.length > 0 ? ` (${disclosures.length})` : ''}</span>
          {disclosuresOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {disclosuresOpen && (
          <div className="px-4 pb-4" data-testid="disclosures-table-container">
            {disclosures.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No active disclosure agreements.</p>
            ) : (
              <table className="w-full text-sm" data-testid="disclosures-table">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="pb-2 pr-4 font-medium">Entity</th>
                    <th className="pb-2 pr-4 font-medium">Counterparty</th>
                    <th className="pb-2 pr-4 font-medium">Level</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Pending action</th>
                    <th className="pb-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {disclosures.map((d) => {
                    const entityOrg = d.my_org_id
                    const entityName = entityOrg
                      ? (entityMap.get(entityOrg)?.entity_name || entityOrg)
                      : '—'
                    const counterpartyLabel = d.counterparty_name
                      || d.counterparty_email
                      || (d.current_level >= 4 ? 'Organisation (name pending)' : 'Anonymous')
                    return (
                      <tr key={d.agreement_id} className="border-b border-gray-100 last:border-0">
                        <td className="py-2 pr-4 text-xs text-gray-700" data-testid={`entity-name-${d.agreement_id}`}>{entityName}</td>
                        <td className="py-2 pr-4 text-xs text-gray-500 italic" data-testid={`counterparty-${d.agreement_id}`}>{counterpartyLabel}</td>
                        <td className="py-2 pr-4 text-gray-800">{d.current_level}</td>
                        <td className="py-2 pr-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            getDisclosureStatus(d) === 'Active'
                              ? 'bg-green-500/20 text-green-700'
                              : 'bg-amber-500/20 text-amber-700'
                          }`}>
                            {getDisclosureStatus(d)}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-gray-600 text-xs">{getDisclosurePendingAction(d, entityName, counterpartyLabel)}</td>
                        <td className="py-2">
                          <div className="inline-block">
                            <button
                              disabled
                              className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-400 cursor-not-allowed"
                              data-testid={`advance-btn-${d.agreement_id}`}
                            >
                              Advance on behalf of {entityName}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {selectedProjectIds.size > 0 && (
        <BulkActionBar
          selectedProjectIds={[...selectedProjectIds]}
          projects={filteredProjects}
          entityTags={entityTags}
          activeAggregatorId={activeAggregatorId}
          onComplete={() => {
            exitSelectionMode()
            queryClient.invalidateQueries({ queryKey: ['aggregator-portfolio', activeAggregatorId] })
          }}
        />
      )}

      {actionError && (
        <div
          data-testid="aggregator-action-error"
          role="alert"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg"
        >
          <AlertCircle size={16} className="shrink-0" />
          {actionError}
          <button
            onClick={() => setActionError(null)}
            className="ml-2 text-red-400 hover:text-red-600"
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  )
}
